import {
  Cart,
  Checkout,
  CheckoutFactory,
  CurrencyCode,
  PaymentFactory,
  IPaymentTransaction,
} from "../../../typescript-library";
import {sentryAddPaymentBreadcrumb} from "../../../helpers/sentryHelper";
import braintree, {Client} from "braintree-web";
import {getShopId} from "../getShopId";
import CreditCardModule from "./CreditCardModule";
import DefaultPaymentModule from "./DefaultPaymentModule";
import {IPaymentModule} from "./IPaymentModule";

class PaymentController {
  private braintreeClient: Client | null = null;
  private paymentId?: number;
  private paymentModule: IPaymentModule | null = null;
  private checkout: Checkout | null = null;
  private initPaymentResponse: IPaymentTransaction | null = null;

  public createPayment(checkout: Checkout, paymentId?: number): IPaymentModule | null {
    if (this.paymentModule && this.paymentId === paymentId) {
      return this.paymentModule;
    }

    this.checkout = checkout;
    this.paymentId = paymentId || this.checkout.paymentId || undefined;

    if (!this.paymentId) {
      return null;
    }

    sentryAddPaymentBreadcrumb("createPayment", checkout, {
      modulePaymentId: this.paymentId,
      paramPaymentId: paymentId,
      checkoutPaymentId: checkout.paymentId,
    });

    // See also `Summary.tsx::purchaseButtonMemoized` and the `PurchaseButton` component of either module
    if ((this.paymentId as Payments) === Payments.CreditCardBraintree) {
      this.paymentModule = new CreditCardModule(checkout);
    } else {
      this.paymentModule = new DefaultPaymentModule(checkout);
    }

    return this.paymentModule;
  }

  public getPayment(): IPaymentModule | null {
    return this.paymentModule;
  }

  /**
   * init payment
   */
  public async initPayment(): Promise<IPaymentTransaction> {
    if (!this.checkout) {
      throw new Error("Payment initialization not possible because of missing checkout data");
    }

    this.initPaymentResponse = await PaymentFactory.initPayment(getShopId().toString(), this.checkout.checkoutId);
    sentryAddPaymentBreadcrumb("initPayment", this.checkout, {
      initPaymentResponse: this.initPaymentResponse,
    });
    if (this.initPaymentResponse.state !== "created") {
      throw new Error("initPayment should return the state 'created' but returned: " + this.initPaymentResponse.state);
    }

    return this.initPaymentResponse;
  }

  /**
   * authorize payment
   * @param visitorId
   * @param nonce
   */
  public async authorizePayment(visitorId: string, nonce?: string): Promise<IPaymentTransaction> {
    if (!this.checkout) {
      throw new Error("Payment authorization not possible because of missing checkout data");
    }

    if (!this.initPaymentResponse?.paymentTransactionId) {
      throw new Error("initPayment has not been called, or received a bad response");
    }

    const authorizePaymentResponse = await PaymentFactory.authorizePayment(
      this.initPaymentResponse.paymentTransactionId,
      visitorId,
      nonce,
    );
    sentryAddPaymentBreadcrumb("authorizePayment", this.checkout, {
      authorizePaymentResponse,
    });
    if (authorizePaymentResponse.state !== "authorized") {
      throw new Error(
        "authorizePayment should return the state 'authorized' but returned: " + authorizePaymentResponse.state,
      );
    }
    if (!this.checkout) {
      throw new Error("No checkout object present");
    }

    return authorizePaymentResponse;
  }

  /**
   * init braintree client
   */
  public async initBraintreeClient(currencyCode?: CurrencyCode): Promise<{client: Client; token: string}> {
    const cartCurrencyCode = this.checkout?.cartData.currencyCode || currencyCode;
    if (!cartCurrencyCode) {
      throw new Error("Missing currencyCode for BraintreeClient initialisation");
    }

    const {clientToken} = await PaymentFactory.getBraintreeToken(cartCurrencyCode);
    this.braintreeClient = await braintree.client.create({authorization: clientToken});
    if (this.checkout) {
      sentryAddPaymentBreadcrumb("initBraintreeClient", this.checkout, {
        braintreeClientCreation: !!this.braintreeClient ? "successful" : "failed",
        cartCurrencyCode,
      });
    }

    return {client: this.braintreeClient, token: clientToken};
  }

  public getPaymentId(): number | undefined {
    return this.paymentId;
  }

  public isDataComplete(): boolean {
    return !!this.paymentModule?.isDataComplete();
  }
}

const paymentController = new PaymentController();
export {paymentController as PaymentController};

/**
 * Generates checkout, sets paymentId on checkout, and creates PaymentController payment
 * @param cart Necessary for `.generateCheckout()`
 * @param paymentId will be instantly set on checkout
 */
export async function retrieveCheckout(cart: Cart, paymentId: Payments) {
  const checkout = await CheckoutFactory.generateCheckout(getShopId(), cart, {
    paymentId,
  });
  // tslib bug fix: if checkout already exists, there
  // is no put of paymentId. So here is next try
  if (checkout.paymentId !== paymentId) {
    await checkout.put({paymentId});
  }
  paymentController.createPayment(checkout, paymentId);

  return checkout;
}

export type LocalDataType = {locale: string; url: string; flag: "de" | "at" | "ch" | "fr" | "dk" | "nl" | null};
type IsoLocalesType = {
  [key: number]: LocalDataType;
};
export const isoLocales: IsoLocalesType = {
  100001: {locale: "de-DE", url: "www.connox.de", flag: "de"},
  100009: {locale: "de-AT", url: "www.connox.at", flag: "at"},
  100017: {locale: "de-CH", url: "www.connox.ch", flag: "ch"},
  100027: {locale: "fr-CH", url: "fr.connox.ch", flag: "ch"},
  100005: {locale: "en-US", url: "www.connox.com", flag: null},
  /* 100020: {locale: "en-GB", url: "www.connox.co.uk", flag: null}, (documentational purposes) */
  100008: {locale: "fr-FR", url: "www.connox.fr", flag: "fr"},
  100022: {locale: "da-DK", url: "www.connox.dk", flag: "dk"},
  100029: {locale: "nl-NL", url: "www.connox.nl", flag: "nl"},
};

export enum Payments {
  Advance = 100000,
  AmazonPay = 100025,
  CreditCard = 100023,
  CreditCardBraintree = 100031,
  Invoice = 100003,
  Paypal = 100004,
  PaypalBraintree = 100030,
  PaypalExpressBraintree = 100032,
  ApplePay = 100043,
  GooglePay = 100044,
  Paydirekt = 100027,
  SEPA = 100017,
}

// TODO cleanup
export const INTERNAL_ERROR_TYPE = {
  PAYPAL: {
    OnApprove: "PayPalOnApprove",
    OnCreateOrder: "PayPalOnCreateOrder",
    PayPalRelated: "PayPalRelated",
    OnCatch: "PayPalOnCatch",
    OnReEnter: "PayPalCartChange",
  },
  PP_EXPRESS: {
    OnApprove: "PPXOnApprove",
    OnCreateOrder: "PPXOnCreateOrder",
    OnShippingChanged: "PPXOnShippingChanged",
    PayPalRelated: "PPXRelated",
    OnPurchase: "PPXOnPurchaseInSummary",
    OnError: "PPExpressOnErrorCallback",
    OnCatch: "PPExpressOnCatch",
    OnBeforeOpen: "PPExpressBeforeOpen",
  },
  SHIPPING: {
    OnCatch: "Shipping",
  },
  CREDIT_CARD: {
    OnCatch: "CreditCard",
    OnNext: "CreditCardLiability",
  },
  GOOGLE_PAY: {
    OnPurchase: "GPayOnPurchase",
  },
  APPLE_PAY: {
    OnInit: "ApplePayInit",
    OnValidation: "ApplePayValidation",
    OnCompleteValidation: "ApplePayCompleteValidation",
    OnCouponSelect: "ApplePayCouponSelect",
    OnPutPaymentId: "ApplePayPutPaymentId",
    OnShippingMethodSelect: "ApplePayShippingMethodSelect",
    OnShippingContactSelect: "ApplePayShippingContactSelect",
    OnPaymentSelect: "ApplePayPaymentSelect",
    OnAuthorize: "ApplePayAuthorize",
  },
  DEFAULT: {
    OnCatch: "DefaultPayment",
  },
};

export const displayName = "connox - be unique";
